import iframeResize from 'iframe-resizer/js/iframeResizer';
import { initIframeEventListener } from './iframeEventListener';
import { initExternalEventBus } from './externalEventBus';
import { initAffiliateListener } from './affiliateListener';
import { initLegacyAffiliateListener } from './affiliateLegacyListener';
import { attachCookiebotParentListeners } from './cookiebotListeners';
import { attachHubspotListeners } from './hubspotEventListeners';
import * as Sentry from '@sentry/browser';

export const IFRAME_STYLE = 'width: 100%; min-height: 600px; border: 0px;';

// fritsAppOptions.display: { plain, header, ctas }. Without `plain`, the other options have no effect.
export function displayParams(display = {}) {
  if (!display?.plain)
    return {};

  const params = { plain: '1' };

  if (display.header === false)
    params.header = '0';

  if (Array.isArray(display.ctas) && display.ctas.length)
    params.ctas = display.ctas.join(',');

  return params;
}

export function createIframe(path, iframeDomain, utm = {}, display = {}) {
  // Create src
  const src = new URL(iframeDomain ?? `https://${process.env.DOMAIN}`);
  // Nuxt redirects paths without a trailing slash (301). Add it here to save that round trip.
  src.pathname = path.endsWith('/') ? path : `${path}/`;
  src.searchParams.append('referrer', global.location.href);
  if (Object.values(utm).length) {
    const parsedUtm = {
      // use source as campaign ifm not set
      ...{ campaign: utm.source },
      ...{ content: 'tool', medium: 'referral' },
      ...utm,
    };
    Object.keys(parsedUtm).forEach(key => src.searchParams.append(`utm_${key}`, parsedUtm[key]));
  }
  Object.entries(displayParams(display)).forEach(([key, value]) => src.searchParams.append(key, value));

  // Create frame
  const iframe = global.document.createElement('iframe');
  iframe.src = src.href;
  iframe.style = IFRAME_STYLE;

  return iframe;
}

export function mountIframe({ id, path, iframeDomain, minHeight = 250, utm = {}, display = {} }) {
  if (!global.document)
    return;

  const containerEl = global.document.getElementById(id);

  if (!containerEl) {
    // eslint-disable-next-line no-console
    console.warn('Couldn\'t mount vue iframe entry, el not found', { containerEl, id });
    return;
  }

  const iframe = createIframe(path, iframeDomain, utm, display);
  containerEl.classList.remove('loading');
  containerEl.append(iframe);

  let frameInitialized = false;

  iframeResize({
    checkOrigin: false,
    // 'grow' never shrinks the iframe. Plain embeds sit between other content on the parent page, so they must shrink.
    // 'lowestElement' also measures fixed widgets of third-party scripts, which follow the iframe height and grow it
    // without end. 'taggedElement' measures only the `data-iframe-height` element in nuxt/layouts/iframe.vue.
    heightCalculationMethod: display?.plain ? 'taggedElement' : 'grow',
    minHeight,
    onInit: () => frameInitialized = true,
    // log: loggerLevel === 'info',
  }, iframe);

  setTimeout(() => {
    if (frameInitialized)
      return;

    Sentry.setContext('iFrame', { path });
    Sentry.captureException(new Error('iFrame not initialized'));
  }, 5000);

  return iframe;
}

export function initEventHandlers({ iframe, appOptions }) {
  // one event bus for an iframe
  const $bus = initExternalEventBus();
  initIframeEventListener({ iframe, $bus, appOptions });

  // attach consumers
  initAffiliateListener({ $bus, appOptions });
  initLegacyAffiliateListener({ $bus, appOptions });
  attachCookiebotParentListeners({ iframe });
  attachHubspotListeners();
}
